import { ColumnToneSchema, type ColumnTone } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Input } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

import { LABEL_MAX, type BoardColumnRow } from '../hooks/use_org_board_config_page';

export interface ColumnCardProps {
  row: BoardColumnRow;
  /** 1-based position within its group, shown as `#N` in the head. */
  position: number;
}

const DOT_CLASS: Record<ColumnTone, string> = {
  gray: 'bg-tone-gray-fg',
  brand: 'bg-primary',
  amber: 'bg-tone-amber-fg',
  green: 'bg-tone-green-fg',
  red: 'bg-tone-red-fg',
  ai: 'bg-ai',
};

/**
 * One column editor: a tone dot + position + stable-id badge header,
 * an editable display label, and (for card columns only) the indicator
 * colour swatches. Clearing the label and blurring restores the VOLL
 * default so a column is never saved blank.
 */
export function ColumnCard({ row, position }: Readonly<ColumnCardProps>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3.5 rounded-lg border border-border-soft bg-paper px-3.5 py-3.5 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden
            className={twcn('h-2 w-2 rounded-pill flex-shrink-0', DOT_CLASS[row.tone])}
          />
          <span className="font-mono text-2xs font-medium text-faint-foreground">#{position}</span>
        </div>
        <span className="inline-flex items-center rounded-pill bg-muted px-1.5 py-0.5 font-mono text-card-id font-medium lowercase text-faint-foreground truncate">
          {row.stableId}
        </span>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-meta font-medium text-muted-foreground">
          {t('orgBoardConfig.field.label')}
        </span>
        <Input
          size="sm"
          value={row.displayedLabel}
          spellCheck={false}
          maxLength={LABEL_MAX}
          aria-label={t('orgBoardConfig.row.labelInput.aria', { id: row.stableId })}
          onChange={(e) => {
            row.setLabel(e.target.value);
          }}
          onBlur={(e) => {
            if (e.target.value.trim() === '') row.setLabel(row.defaultLabel);
          }}
        />
      </label>

      {row.setTone ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-muted-foreground">
            {t('orgBoardConfig.column.toneLabel')}
          </span>
          <div
            className="flex items-center gap-1.5"
            role="radiogroup"
            aria-label={t('orgBoardConfig.column.toneGroup.aria', { id: row.stableId })}
          >
            {ColumnToneSchema.options.map((tone) => (
              <button
                key={tone}
                type="button"
                role="radio"
                aria-checked={row.tone === tone}
                aria-label={tone}
                title={tone}
                onClick={() => row.setTone?.(tone)}
                className={twcn(
                  'h-5 w-5 rounded-pill ring-offset-1 ring-offset-paper',
                  'transition-shadow duration-fast ease-standard',
                  DOT_CLASS[tone],
                  row.tone === tone
                    ? 'ring-2 ring-foreground'
                    : 'ring-1 ring-border-soft hover:ring-muted-foreground',
                )}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
